import {
    initializeFaro,
    getWebInstrumentations,
    ReactIntegration,
    createReactRouterV7Options,
    type Faro,
    type TransportItem,
} from "@grafana/faro-react";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import {
    Routes,
    matchRoutes,
    createRoutesFromChildren,
    useLocation,
    useNavigationType,
} from "react-router-dom";

const SENSITIVE_HEADER_RE = /(authorization|cookie|x-csrf)/i;
const URL_KEY_RE = /^(url|href|page\.url|http\.url|http\.target)$/i;
const BOT_UA_RE =
    /bot\b|crawl|spider|slurp|prerender|headless|read-aloud|facebookexternalhit|whatsapp|chrome-lighthouse/i;
const FARO_URL = "https://faro.energiegemeinschaft.info/collect";

// Emits one event per fetch/xhr resource timing, which duplicates the span the
// tracing instrumentation already produces for the same request — and the span
// is the one that correlates with the backend trace. Dropping it removes about
// half of all ingested telemetry without losing information.
const PERFORMANCE_INSTRUMENTATION =
    "@grafana/faro-web-sdk:instrumentation-performance";

// Browser noise that is not actionable and cannot be fixed in application code.
const IGNORED_ERRORS = [
    // Benign: fired when observer callbacks outlive a frame. No user impact.
    /ResizeObserver loop/,
    // Cross-origin script error with no stack — carries no usable information.
    // Faro matches against "message name stack", so the pattern must not be anchored at the end.
    /^Script error\.?(\s|$)/,
    // Injected by crypto-wallet browser extensions, not our code.
    /window\.ethereum/,
];

let faroInstance: Faro | null = null;

function detectEnvironment(): "dev" | "prod" | "local" {
    if (typeof window === "undefined") return "local";
    const host = window.location.hostname;
    if (host === "app.energiegemeinschaft.info") return "prod";
    if (host === "app-dev.energiegemeinschaft.info") return "dev";
    return "local";
}

export function initFaro(): Faro | null {
    if (faroInstance) return faroInstance;

    const environment = detectEnvironment();
    // Skip telemetry from local dev / unknown hosts. Avoids polluting Loki
    // and accidentally hitting the prod receiver from a developer machine.
    if (environment === "local") return null;

    // Skip bots (Google-Read-Aloud, Googlebot, prerenderers, headless Chrome,
    // social-card fetchers). Restricted UAs commonly fail on service-worker
    // registration / storage and surface as Faro exceptions without affecting
    // real users.
    if (isBotUserAgent(window.navigator.userAgent)) return null;

    faroInstance = initializeFaro({
        url: FARO_URL,
        app: {
            // Env is part of the name so it surfaces as a distinct Loki/Tempo
            // service identifier (matches backend convention
            // ecsf-platform-{dev,prod}).
            name: `ecsf-platform-frontend-${environment}`,
            version: import.meta.env.VITE_APP_VERSION ?? "unknown",
            environment,
        },
        instrumentations: [
            ...getWebInstrumentations({ captureConsole: false }).filter(
                (instrumentation) =>
                    instrumentation.name !== PERFORMANCE_INSTRUMENTATION,
            ),
            new TracingInstrumentation({
                instrumentationOptions: {
                    propagateTraceHeaderCorsUrls: [
                        /^https:\/\/app-dev\.energiegemeinschaft\.info\//,
                        /^https:\/\/app\.energiegemeinschaft\.info\//,
                    ],
                    // Drops the per-request timing sub-events (domainLookup,
                    // connect, ...) from every span. The span itself is kept.
                    fetchInstrumentationOptions: { ignoreNetworkEvents: true },
                    xhrInstrumentationOptions: { ignoreNetworkEvents: true },
                },
            }),
            new ReactIntegration({
                router: createReactRouterV7Options({
                    Routes,
                    matchRoutes,
                    createRoutesFromChildren,
                    useLocation,
                    useNavigationType,
                }),
            }),
        ],
        sessionTracking: { enabled: true, persistent: false },
        ignoreErrors: IGNORED_ERRORS,
        beforeSend: sanitizeEvent,
    });

    return faroInstance;
}

export function getFaro(): Faro | null {
    return faroInstance;
}

export function setFaroForTesting(instance: Faro | null) {
    faroInstance = instance;
}

export function sanitizeEvent(payload: TransportItem): TransportItem | null {
    walkAndSanitize(payload as unknown as Record<string, unknown>);
    return payload;
}

export function isBotUserAgent(ua: string | undefined | null): boolean {
    if (!ua) return false;
    return BOT_UA_RE.test(ua);
}

function walkAndSanitize(obj: Record<string, unknown>, depth = 0): void {
    if (depth > 8 || obj === null || typeof obj !== "object") return;
    for (const key of Object.keys(obj)) {
        const val = obj[key];

        if (typeof val === "object" && val !== null) {
            if (SENSITIVE_HEADER_RE.test(key)) {
                delete obj[key];
                continue;
            }
            if (Array.isArray(val)) {
                for (const item of val) {
                    if (item && typeof item === "object") {
                        walkAndSanitize(
                            item as Record<string, unknown>,
                            depth + 1,
                        );
                    }
                }
            } else {
                walkAndSanitize(val as Record<string, unknown>, depth + 1);
            }
            continue;
        }

        if (typeof val === "string") {
            if (SENSITIVE_HEADER_RE.test(key)) {
                delete obj[key];
                continue;
            }
            if (URL_KEY_RE.test(key)) {
                obj[key] = sanitizeUrlString(val);
            }
        }
    }
}

function sanitizeUrlString(u: string): string {
    return (
        u
            .replace(
                /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
                "/:guid",
            )
            .replace(/\/\d+(?=\/|$|\?|#)/g, "/:id")
            .split("?")[0]
            ?.split("#")[0] ?? ""
    );
}
